import React, { useState } from "react";
import {
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { IContractParty } from "dto/Contracts/IContract";
import { axiosInstance } from "api/axios";
import { NIPMaskedInput } from "./MaskedInputs";
import styles from "./styles.module.scss";

const NIP_REGEX = /^\d{10}$/;

const PODMIOT_OPTIONS = ["Przedsiębiorca", "Osoba fizyczna", "JSFP"];

export const emptyMember = (): IContractParty => ({
  Name: "",
  IdentifierType: "NIP",
  IdentifierValue: "",
  Role: "",
});

const genId = () => `t-${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface MemberTab {
  id: string;
  data: IContractParty;
  saved: boolean;
}

interface Props {
  onDataChange: (name: string, members: IContractParty[]) => void;
  onRemove: () => void;
}

const ConsortiumPartyCard: React.FC<Props> = ({ onDataChange, onRemove }) => {
  const [consortiumName, setConsortiumName] = useState("");
  const [tabs, setTabs] = useState<MemberTab[]>([{ id: genId(), data: emptyMember(), saved: false }]);
  const [savedOrder, setSavedOrder] = useState<string[]>([]);
  const [leaderId, setLeaderId] = useState<string | null>(null);
  const [activeTabIdx, setActiveTabIdx] = useState(1); // 0 = Lista, 1..N = member tabs
  const [nipLoadingTabsIdx, setNipLoadingTabsIdx] = useState<number | null>(null);

  const report = (
    name: string,
    currentTabs: MemberTab[],
    currentSavedOrder: string[],
    currentLeaderId: string | null,
  ) => {
    const members = currentSavedOrder
      .map((id) => currentTabs.find((t) => t.id === id))
      .filter((t): t is MemberTab => !!t)
      .map((t) => ({
        ...t.data,
        ...(t.id === currentLeaderId && { IsConsortiumLeader: true }),
      }));
    onDataChange(name, members);
  };

  const handleNameChange = (name: string) => {
    setConsortiumName(name);
    report(name, tabs, savedOrder, leaderId);
  };

  const handleFieldChange = (tabsIdx: number, field: keyof IContractParty, value: string) => {
    setTabs((prev) => prev.map((t, i) => (i === tabsIdx ? { ...t, data: { ...t.data, [field]: value } } : t)));
  };

  const handleNipBlur = async (tabsIdx: number, value: string) => {
    const tab = tabs[tabsIdx];
    if (tab.data.IdentifierType !== "NIP") return;
    const clean = value.replace(/[-\s]/g, "");
    if (!NIP_REGEX.test(clean) || tab.data.Name.trim()) return;

    setNipLoadingTabsIdx(tabsIdx);
    try {
      const res = await axiosInstance.get(`/bir/search?nip=${clean}`);
      const name: string = res?.data?.Name ?? "";
      if (name) handleFieldChange(tabsIdx, "Name", name);
    } catch {
      // silent
    } finally {
      setNipLoadingTabsIdx(null);
    }
  };

  const handleSave = (tabsIdx: number) => {
    const tab = tabs[tabsIdx];
    const isFirst = !tab.saved;

    const newTabs = tabs.map((t, i) => (i === tabsIdx ? { ...t, saved: true } : t));
    const newSavedOrder = isFirst ? [...savedOrder, tab.id] : savedOrder;

    let finalTabs = newTabs;
    let nextActive = activeTabIdx;

    if (isFirst && tabsIdx === tabs.length - 1) {
      finalTabs = [...newTabs, { id: genId(), data: emptyMember(), saved: false }];
      nextActive = activeTabIdx + 1;
    }

    setTabs(finalTabs);
    setSavedOrder(newSavedOrder);
    setActiveTabIdx(nextActive);
    report(consortiumName, finalTabs, newSavedOrder, leaderId);
  };

  const handleSetLeader = (id: string | null) => {
    setLeaderId(id);
    report(consortiumName, tabs, savedOrder, id);
  };

  const handleMoveUp = (savedIdx: number) => {
    if (savedIdx === 0) return;
    const newOrder = [...savedOrder];
    [newOrder[savedIdx - 1], newOrder[savedIdx]] = [newOrder[savedIdx], newOrder[savedIdx - 1]];
    setSavedOrder(newOrder);
    report(consortiumName, tabs, newOrder, leaderId);
  };

  const handleMoveDown = (savedIdx: number) => {
    if (savedIdx >= savedOrder.length - 1) return;
    const newOrder = [...savedOrder];
    [newOrder[savedIdx], newOrder[savedIdx + 1]] = [newOrder[savedIdx + 1], newOrder[savedIdx]];
    setSavedOrder(newOrder);
    report(consortiumName, tabs, newOrder, leaderId);
  };

  const handleRemoveMember = (tabsIdx: number) => {
    if (tabs.length <= 1) return;
    const tab = tabs[tabsIdx];
    const newTabs = tabs.filter((_, i) => i !== tabsIdx);
    const newSavedOrder = savedOrder.filter((id) => id !== tab.id);
    const muiIdx = tabsIdx + 1;
    const nextActive = Math.min(Math.max(1, muiIdx <= activeTabIdx ? activeTabIdx - 1 : activeTabIdx), newTabs.length);

    setTabs(newTabs);
    setSavedOrder(newSavedOrder);
    setActiveTabIdx(nextActive);
    report(consortiumName, newTabs, newSavedOrder, leaderId === tab.id ? null : leaderId);
    if (leaderId === tab.id) setLeaderId(null);
  };

  const savedTabs = savedOrder.map((id) => tabs.find((t) => t.id === id)).filter((t): t is MemberTab => !!t);
  const currentTabsIdx = activeTabIdx - 1;
  const currentTab = activeTabIdx > 0 ? tabs[currentTabsIdx] : null;

  return (
    <div className={styles.consortiumCard}>
      {/* Nagłówek z polem nazwy */}
      <div className={styles.consortiumHeader}>
        <span className={styles.consortiumHeaderLabel}>Konsorcjum</span>
        <TextField
          label="Nazwa konsorcjum"
          size="small"
          className={styles.consortiumNameField}
          value={consortiumName}
          onChange={(e) => handleNameChange(e.target.value)}
        />
        <button type="button" className={styles.removeBtn} onClick={onRemove}>Usuń</button>
      </div>

      {/* Pasek zakładek */}
      <div className={styles.consortiumTabsBar}>
        <Tabs
          value={activeTabIdx}
          onChange={(_, v) => setActiveTabIdx(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 30,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: "3px", padding: "5px 10px 0" },
            "& .MuiTab-root": {
              minHeight: 28,
              fontSize: 11,
              padding: "3px 12px",
              textTransform: "none",
              border: "1px solid #d0c8f0",
              borderRadius: "4px 4px 0 0",
              color: "#888",
            },
            "& .MuiTab-root.Mui-selected": {
              background: "#ede9f8",
              color: "#5d49c7",
              borderColor: "#5d49c7",
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Lista" />
          {tabs.map((_, idx) => {
            const isLast = idx === tabs.length - 1;
            return (
              <Tab
                key={idx}
                label={isLast ? "+" : String(idx + 1)}
                className={isLast ? styles.plusTabBtn : undefined}
              />
            );
          })}
        </Tabs>
      </div>

      {/* Zawartość zakładki */}
      <div className={styles.consortiumTabContent}>
        {activeTabIdx === 0 ? (
          /* Zakładka Lista */
          savedTabs.length === 0 ? (
            <span className={styles.consortiumEmptyList}>Brak zapisanych członków</span>
          ) : (
            <div className={styles.consortiumMemberList}>
              {savedTabs.map((t, savedIdx) => (
                <div
                  key={t.id}
                  className={styles.consortiumMemberItem}
                  onClick={() => {
                    const ti = tabs.findIndex((tab) => tab.id === t.id);
                    if (ti >= 0) setActiveTabIdx(ti + 1);
                  }}
                >
                  <div className={styles.consortiumMemberReorder}>
                    <button type="button" className={styles.reorderBtn} onClick={(e) => { e.stopPropagation(); handleMoveUp(savedIdx); }} disabled={savedIdx === 0}>▲</button>
                    <button type="button" className={styles.reorderBtn} onClick={(e) => { e.stopPropagation(); handleMoveDown(savedIdx); }} disabled={savedIdx === savedTabs.length - 1}>▼</button>
                  </div>
                  {leaderId === t.id && <span className={styles.leaderStar}>★</span>}
                  <span className={styles.consortiumMemberName}>{t.data.Name.trim() || `Członek ${savedIdx + 1}`}</span>
                </div>
              ))}
            </div>
          )
        ) : currentTab ? (
          /* Zakładka członka */
          <div className={styles.consortiumMemberForm}>
            <div className={styles.partyRow1}>
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <Select
                  value={currentTab.data.IdentifierType}
                  onChange={(e) => handleFieldChange(currentTabsIdx, "IdentifierType", e.target.value as "NIP" | "PESEL")}
                >
                  <MenuItem value="NIP">NIP</MenuItem>
                  <MenuItem value="PESEL">PESEL</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Numer identyfikatora"
                size="small"
                sx={{ flex: 1 }}
                value={currentTab.data.IdentifierValue}
                onChange={(e) => handleFieldChange(currentTabsIdx, "IdentifierValue", e.target.value)}
                onBlur={(e) => handleNipBlur(currentTabsIdx, e.target.value)}
                InputProps={{
                  ...(currentTab.data.IdentifierType === "NIP" && {
                    inputComponent: NIPMaskedInput as React.ElementType,
                  }),
                  ...(nipLoadingTabsIdx === currentTabsIdx && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ),
                  }),
                }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Typ podmiotu</InputLabel>
                <Select
                  value={currentTab.data.Role}
                  label="Typ podmiotu"
                  onChange={(e) => handleFieldChange(currentTabsIdx, "Role", e.target.value)}
                >
                  {PODMIOT_OPTIONS.map((o) => (
                    <MenuItem key={o} value={o}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <TextField
              label="Nazwa"
              size="small"
              fullWidth
              value={currentTab.data.Name}
              onChange={(e) => handleFieldChange(currentTabsIdx, "Name", e.target.value)}
              placeholder={currentTab.data.IdentifierType === "NIP" ? "Uzupełniane automatycznie z NIP" : ""}
            />

            <div className={styles.consortiumMemberActions}>
              <div>
                {currentTab.saved && (
                  <button
                    type="button"
                    className={`${styles.leaderToggleBtn} ${leaderId === currentTab.id ? styles.leaderToggleBtnActive : ""}`}
                    onClick={() => handleSetLeader(leaderId === currentTab.id ? null : currentTab.id)}
                  >
                    {leaderId === currentTab.id ? "★ Lider" : "☆ Oznacz jako lidera"}
                  </button>
                )}
              </div>
              <div className={styles.consortiumMemberActionsRight}>
                {tabs.length > 1 && (
                  <button type="button" className={styles.removeBtn} onClick={() => handleRemoveMember(currentTabsIdx)}>
                    Usuń
                  </button>
                )}
                <button type="button" className={styles.saveMemberBtn} onClick={() => handleSave(currentTabsIdx)}>
                  {currentTab.saved ? "Zaktualizuj" : "Zapisz"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ConsortiumPartyCard;
